import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { RoomContact, RoomWorkstation, SchulungsraumDefinition } from '../services/types';

export interface RoomEditorDialogProps {
  /** null = create mode, non-null = edit mode */
  room: SchulungsraumDefinition | null;
  open: boolean;
  saving?: boolean;
  /** Called with the validated field values. Caller handles the API call. */
  onSubmit: (values: RoomFormValues) => void;
  onClose: () => void;
}

export interface RoomFormValues {
  name: string;
  location: string;
  capacity: number;
  description: string;
  isActive: boolean;
  contacts: RoomContact[];
  workstations: RoomWorkstation[];
}

// NC-1403: per-seat notes are edited inline; very large rooms only get the
// first rows so the dialog stays usable (the API accepts up to 500 entries).
const MAX_SEAT_ROWS = 100;

interface SeatFields {
  note: string;
  assetRef: string;
}

function seatsFromRoom(room: SchulungsraumDefinition | null): Record<number, SeatFields> {
  const out: Record<number, SeatFields> = {};
  for (const w of room?.workstations ?? []) {
    out[w.seat] = { note: w.note ?? '', assetRef: w.assetRef ?? '' };
  }
  return out;
}

/** Annotated seats within 1..capacity, trimmed; empty seats are left out. */
export function buildWorkstations(seats: Record<number, SeatFields>, capacity: number): RoomWorkstation[] {
  return Object.entries(seats)
    .map(([seat, f]) => ({ seat: Number(seat), note: f.note.trim(), assetRef: f.assetRef.trim() }))
    .filter((w) => w.seat >= 1 && w.seat <= capacity && (w.note !== '' || w.assetRef !== ''))
    .sort((a, b) => a.seat - b.seat)
    .map((w) => ({
      seat: w.seat,
      ...(w.note ? { note: w.note } : {}),
      ...(w.assetRef ? { assetRef: w.assetRef } : {}),
    }));
}

function isValidCapacity(raw: string): boolean {
  if (raw === '' || raw === null || raw === undefined) return false;
  const n = Number(raw);
  if (isNaN(n)) return false;
  if (!Number.isInteger(n)) return false;
  if (n < 0) return false;
  return true;
}

// Intentionally loose (jeder-@-mit-Domainpunkt): the API re-validates via Zod
// .email(); this only gates obviously broken input before submit.
function isValidEmail(raw: string): boolean {
  return /^\S+@\S+\.\S+$/.test(raw.trim());
}

function isValidContact(contact: RoomContact): boolean {
  return (
    contact.firstName.trim().length > 0 &&
    contact.lastName.trim().length > 0 &&
    isValidEmail(contact.email)
  );
}

export function RoomEditorDialog({
  room,
  open,
  saving = false,
  onSubmit,
  onClose,
}: RoomEditorDialogProps) {
  const isEdit = room !== null;

  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [capacityRaw, setCapacityRaw] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [contacts, setContacts] = useState<RoomContact[]>([]);
  const [seats, setSeats] = useState<Record<number, SeatFields>>({});

  // Reset form whenever the dialog opens or the room changes
  useEffect(() => {
    if (open) {
      setName(room?.name ?? '');
      setLocation(room?.location ?? '');
      setCapacityRaw(room !== null ? String(room.capacity) : '');
      setDescription(room?.description ?? '');
      setIsActive(room !== null ? room.isActive : true);
      setContacts(room?.contacts ?? []);
      setSeats(seatsFromRoom(room));
    }
  }, [open, room]);

  const addContact = () => {
    setContacts((prev) => [...prev, { firstName: '', lastName: '', email: '' }]);
  };

  const removeContact = (index: number) => {
    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const updateContact = (index: number, field: keyof RoomContact, value: string) => {
    setContacts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const updateSeat = (seat: number, field: keyof SeatFields, value: string) => {
    setSeats((prev) => ({
      ...prev,
      [seat]: { ...(prev[seat] ?? { note: '', assetRef: '' }), [field]: value },
    }));
  };

  const nameTrim = name.trim();
  const locationTrim = location.trim();
  const capacityValid = isValidCapacity(capacityRaw);
  const contactsValid = contacts.every(isValidContact);
  const formValid =
    nameTrim.length > 0 && locationTrim.length > 0 && capacityValid && contactsValid;

  const handleSubmit = () => {
    if (!formValid) return;
    onSubmit({
      name: nameTrim,
      location: locationTrim,
      capacity: Number(capacityRaw),
      description: description.trim(),
      isActive,
      contacts: contacts.map((c) => ({
        firstName: c.firstName.trim(),
        lastName: c.lastName.trim(),
        email: c.email.trim(),
      })),
      workstations: buildWorkstations(seats, Number(capacityRaw)),
    });
  };

  const capacityNum = capacityValid ? Number(capacityRaw) : 0;
  const seatRows = Math.min(capacityNum, MAX_SEAT_ROWS);
  const droppedSeats = Object.entries(seats).filter(
    ([seat, f]) => Number(seat) > capacityNum && (f.note.trim() !== '' || f.assetRef.trim() !== '')
  ).length;

  const title = isEdit ? 'Schulungsraum bearbeiten' : 'Neuer Schulungsraum';

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="room-editor-dialog-title"
    >
      <DialogTitle id="room-editor-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            fullWidth
            required
            inputProps={{ 'aria-label': 'Name' }}
            autoFocus
          />
          <TextField
            label="Standort"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            disabled={saving}
            fullWidth
            required
            inputProps={{ 'aria-label': 'Standort' }}
          />
          <TextField
            label="Anzahl Arbeitsplaetze"
            type="number"
            value={capacityRaw}
            onChange={(e) => setCapacityRaw(e.target.value)}
            disabled={saving}
            fullWidth
            required
            inputProps={{ 'aria-label': 'Anzahl Arbeitsplaetze', min: 0, step: 1 }}
          />
          <TextField
            label="Beschreibung"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            fullWidth
            multiline
            rows={3}
            inputProps={{ 'aria-label': 'Beschreibung' }}
          />

          {/* NC-1075: verantwortliche Ansprechpartner */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Verantwortliche Ansprechpartner
            </Typography>
            <Stack spacing={1}>
              {contacts.map((contact, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label="Vorname"
                    value={contact.firstName}
                    onChange={(e) => updateContact(index, 'firstName', e.target.value)}
                    disabled={saving}
                    size="small"
                    required
                    inputProps={{ 'aria-label': `Ansprechpartner ${index + 1} Vorname` }}
                  />
                  <TextField
                    label="Nachname"
                    value={contact.lastName}
                    onChange={(e) => updateContact(index, 'lastName', e.target.value)}
                    disabled={saving}
                    size="small"
                    required
                    inputProps={{ 'aria-label': `Ansprechpartner ${index + 1} Nachname` }}
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={contact.email}
                    onChange={(e) => updateContact(index, 'email', e.target.value)}
                    disabled={saving}
                    size="small"
                    required
                    error={contact.email.trim() !== '' && !isValidEmail(contact.email)}
                    inputProps={{ 'aria-label': `Ansprechpartner ${index + 1} Email` }}
                    sx={{ flex: 1 }}
                  />
                  <Tooltip title="Ansprechpartner entfernen">
                    <IconButton
                      aria-label={`Ansprechpartner ${index + 1} entfernen`}
                      onClick={() => removeContact(index)}
                      disabled={saving}
                      size="small"
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
              <Box>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addContact}
                  disabled={saving || contacts.length >= 20}
                  size="small"
                >
                  Ansprechpartner hinzufuegen
                </Button>
              </Box>
            </Stack>
          </Box>

          {/* NC-1403 (D9): per-seat note for the tutor + Matrix42 asset id as text */}
          {seatRows > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Arbeitsplaetze (Hinweis und Matrix42-Kennung)
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Optional. Der Hinweis ist fuer Tutoren sichtbar (z.B. &quot;gesperrt - Monitor defekt&quot;),
                die Kennung dient dem Nachschlagen in Matrix42.
              </Typography>
              <Stack spacing={1}>
                {Array.from({ length: seatRows }, (_, i) => i + 1).map((seat) => (
                  <Stack key={seat} direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ minWidth: 64 }}>
                      Platz {seat}
                    </Typography>
                    <TextField
                      label="Hinweis"
                      value={seats[seat]?.note ?? ''}
                      onChange={(e) => updateSeat(seat, 'note', e.target.value)}
                      disabled={saving}
                      size="small"
                      sx={{ flex: 2 }}
                      inputProps={{ 'aria-label': `Platz ${seat} Hinweis`, maxLength: 200 }}
                    />
                    <TextField
                      label="Matrix42-Kennung"
                      value={seats[seat]?.assetRef ?? ''}
                      onChange={(e) => updateSeat(seat, 'assetRef', e.target.value)}
                      disabled={saving}
                      size="small"
                      sx={{ flex: 1 }}
                      inputProps={{ 'aria-label': `Platz ${seat} Matrix42-Kennung`, maxLength: 100 }}
                    />
                  </Stack>
                ))}
              </Stack>
              {capacityNum > MAX_SEAT_ROWS && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                  Es werden nur die ersten {MAX_SEAT_ROWS} Plaetze angezeigt.
                </Typography>
              )}
            </Box>
          )}
          {droppedSeats > 0 && (
            <Typography variant="caption" color="warning.main" component="div">
              {droppedSeats} Hinweis(e) zu Plaetzen ueber der Anzahl werden beim Speichern entfernt.
            </Typography>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="Aktiv"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={saving || !formValid}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          {saving ? 'Speichern…' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RoomEditorDialog;
